import { css } from 'lit';

/**
 * Shared hover tooltip for the desktop sidebar selectors (aircraft / upgrade /
 * cruise). All three rendered an identical `.tooltip` / `.arrow` / `.header` /
 * `.item` / `.desc` / `.clickto` block; this centralizes it.
 *
 * Each consumer keeps its own bits:
 * - the `.X:hover .tooltip { display: block }` trigger, since the parent class
 *   differs. All three gate it under `@media (hover: hover)`: the sidebar is
 *   desktop-only (airmash-game skips it on mobile), but a touch-only device the
 *   mobile check doesn't catch still renders it, and a tap there would leave
 *   the card stuck open;
 * - any selector-specific tooltip children (aircraft stat bars, upgrade level
 *   badges);
 * - the width, if it isn't the default 180px — set `--tooltip-width` on the host
 *   (cruise uses 200px).
 *
 * Usage: `static override styles = [tokens, selectorTooltip, css\`...\`];`
 */
export const selectorTooltip = css`
  .tooltip {
    display: none;
    position: absolute;
    top: -10px;
    left: 54px;
    width: var(--tooltip-width, 180px);
    background-color: var(--color-bg-panel);
    border-radius: var(--radius-lg);
    border-bottom: 2px solid var(--color-border-strong);
    border-top: 2px solid var(--color-border-light);
    padding: 10px 14px;
    cursor: default;
    z-index: 20;
  }

  /* Invisible strip over the 10px gap to the button, full button height. The
     card is a child of the button, so hover survives the move onto it only if
     the pointer never lands on the gap — and the arrow alone covers just the
     middle 20px of the 44px button, so leaving from its top or bottom dropped
     the card before you reached it (a long CTF roster needs the pointer on
     the card to scroll). top: 8px lines up with the button: the card sits at
     top -10px, and this is measured inside its 2px top border. */
  .tooltip::before {
    content: '';
    position: absolute;
    top: 8px;
    left: -10px;
    width: 10px;
    height: 44px;
  }

  .tooltip .arrow {
    position: absolute;
    top: 22px;
    left: -10px;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid var(--color-bg-panel);
  }

  .tooltip .header {
    font-weight: bold;
    font-size: var(--font-size-lg);
    color: var(--color-text-white);
    padding-bottom: 6px;
  }

  .tooltip .item {
    margin-top: 10px;
  }

  .tooltip .item .desc {
    color: var(--color-text-tooltip);
  }

  .tooltip .item .clickto {
    padding-top: 12px;
    color: var(--color-text-secondary);
  }
`;

/**
 * Plane and upgrade icons, shared by the desktop sidebar (aircraft- and
 * upgrade-selector) and the mobile loadout popover (loadout-panel). Keyed by
 * `cssClass` / `key` from loadout-data, so an icon element just carries that
 * class next to its own sizing class. The selectors are bare classes because
 * each consumer's shadow root scopes them. Only background-image is set here;
 * the consumer owns size, repeat and colour.
 */
export const selectorIcons = css`
  .predator {
    background-image: url('/vanilla/ui/selectors/predator-select.png');
  }
  .goliath {
    background-image: url('/vanilla/ui/selectors/goliath-select.png');
  }
  .mohawk {
    background-image: url('/vanilla/ui/selectors/mohawk-select.png');
  }
  .tornado {
    background-image: url('/vanilla/ui/selectors/tornado-select.png');
  }
  .prowler {
    background-image: url('/vanilla/ui/selectors/prowler-select.png');
  }

  .speed {
    background-image: url('/vanilla/ui/selectors/speed-select.png');
  }
  .defense {
    background-image: url('/vanilla/ui/selectors/defence-select.png');
  }
  .energy {
    background-image: url('/vanilla/ui/selectors/energy-select.png');
  }
  .missile {
    background-image: url('/vanilla/ui/selectors/missile-select.png');
  }
`;

/**
 * Badges drawn inside the selector icons, which must be position: relative.
 * `.count-badge` is the per-team CTF plane count (aircraft-selector,
 * loadout-panel); `.level` is the upgrade level counter (upgrade-selector,
 * loadout-panel). Decorative, so taps and clicks pass through to the button.
 */
export const selectorBadges = css`
  .count-badge {
    position: absolute;
    bottom: 1px;
    display: inline-block;
    padding: 1px 4px;
    background-color: var(--color-bg-selector-hover);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-xxs);
    font-weight: 700;
    line-height: 1;
    text-shadow: var(--shadow-text);
    pointer-events: none;
  }

  .count-badge.blue {
    left: 1px;
    color: var(--color-team-blue);
  }

  .count-badge.red {
    right: 1px;
    color: var(--color-team-red);
  }

  .level {
    display: inline-block;
    position: absolute;
    right: 2px;
    bottom: 2px;
    background-color: var(--color-bg-selector-hover);
    padding: 2px 0;
    border-radius: var(--radius-sm);
    width: 18px;
    text-align: center;
    color: var(--color-text-bright);
    font-size: var(--font-size-xs);
    pointer-events: none;
  }
`;
